function buildQuery(params = {}, map = {}) {
  const query = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === "") return;
    const queryKey = map[key] || key;
    query.push(`${encodeURIComponent(queryKey)}=${encodeURIComponent(value)}`);
  });
  return query.length ? `?${query.join("&")}` : "";
}

function toImportFormData(file) {
  const formData = new FormData();
  formData.set("file", file);
  return formData;
}

function previewTokenPayload(previewToken, params = {}) {
  return {
    PreviewToken: previewToken,
    ...params,
  };
}

function acceptsAsyncCommit(options = {}) {
  return {
    ...options,
    isSuccess: (res) => res?.status === 200 || res?.status === 202,
  };
}

async function postBlob(client, address, payload, options = {}) {
  client._requestLog("post", address, payload, options);
  const response = await client._doRequestWithRetry(
    {
      method: "post",
      url: `${client._url}${address}`,
      data: payload,
      responseType: "blob",
    },
    options,
  );
  client._responseLog("post", address, response);
  return response;
}

async function getBlob(client, address, options = {}) {
  client._requestLog("get", address, null, options);
  let response;
  try {
    response = await client._doRequestWithRetry(
      {
        method: "get",
        url: `${client._url}${address}`,
        responseType: "blob",
      },
      options,
    );
  } catch (err) {
    throw await normalizeBlobApiError(err);
  }
  client._responseLog("get", address, response);
  return response;
}

async function normalizeBlobApiError(err) {
  const data = err?.response?.data || err?.data;
  if (!data || typeof Blob === "undefined" || !(data instanceof Blob)) return err;
  const contentType = String(err?.headers?.["content-type"] || err?.response?.headers?.["content-type"] || data.type || "");
  if (!contentType.includes("application/json")) return err;
  try {
    const parsed = JSON.parse(await data.text());
    if (err.response) err.response.data = parsed;
    else err.data = parsed;
  } catch (_) {
    return err;
  }
  return err;
}

/**
 * @function ApiClient#GetCareCardList
 * @this {ApiClient}
 * @param {any} cid
 * @param {object} params 參數。
 * @param {any} params.page
 * @param {any} params.perPage
 * @param {any} params.search
 * @param {any} options = {}
 */
export async function GetCareCardList(cid, { page = 1, perPage = 15, search } = {}, options = {}) {
  const query = [`p=${page}`, `pp=${perPage}`];
  if (search) query.push(`search=${encodeURIComponent(search)}`);
  return await this._get(`/community/${cid}/access/care_card_list?${query.join("&")}`, options);
}

/**
 * @function ApiClient#CreateCareCard
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} payload
 */
export async function CreateCareCard(cid, payload) {
  return await this._post(`/community/${cid}/access/care_card`, payload);
}

/**
 * @function ApiClient#UpdateCareCard
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} cardID
 * @param {any} payload
 */
export async function UpdateCareCard(cid, cardID, payload) {
  return await this._put(`/community/${cid}/access/care_card/${cardID}`, payload);
}

/**
 * @function ApiClient#DeleteCareCard
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} cardID
 */
export async function DeleteCareCard(cid, cardID) {
  return await this._delete(`/community/${cid}/access/care_card/${cardID}`);
}

/**
 * @function ApiClient#GetAccessZoneList
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} options = {}
 */
export async function GetAccessZoneList(cid, options = {}) {
  const res = await this.GetTSMDeviceList(cid, {}, options);
  const devices = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : Array.isArray(res?.result) ? res.result : [];
  return devices
    .map((d) => ({
      id: String(d.id || d.ID || d.CardmachineNumber || d.DeviceID || ""),
      Name: d.Name || d.CardmachineNumber || d.DeviceID || d.ID || "",
    }))
    .filter((d) => d.id && d.Name);
}

/**
 * @function ApiClient#GetAccessCareCards
 * @this {ApiClient}
 * @param {any} cid
 * @param {object} params 參數。
 * @param {any} params.page
 * @param {any} params.perPage
 * @param {any} params.search
 * @param {any} params.careType
 * @param {any} params.enabled
 * @param {any} params.houseHold
 * @param {any} params.templateID
 * @param {any} options = {}
 */
export async function GetAccessCareCards(cid, { page = 1, perPage = 20, search, careType, enabled, houseHold, templateID } = {}, options = {}) {
  return await this._get(
    `/community/${cid}/access/care/cards${buildQuery({
      p: page,
      pp: perPage,
      search,
      CareType: careType,
      Enabled: enabled,
      HouseHold: houseHold,
      TemplateID: templateID,
    })}`,
    options,
  );
}

/**
 * @function ApiClient#GetAccessCareCardListV2
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} params = {}
 * @param {any} options = {}
 */
export async function GetAccessCareCardListV2(cid, params = {}, options = {}) {
  return await this.GetAccessCareCards(cid, params, options);
}

/**
 * @function ApiClient#GetAccessCareCard
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} cardID
 * @param {any} options = {}
 */
export async function GetAccessCareCard(cid, cardID, options = {}) {
  return await this._get(`/community/${cid}/access/care/cards/${cardID}`, options);
}

/**
 * @function ApiClient#GetAccessCareCardDetail
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} cardID
 * @param {any} options = {}
 */
export async function GetAccessCareCardDetail(cid, cardID, options = {}) {
  return await this.GetAccessCareCard(cid, cardID, options);
}

/**
 * @function ApiClient#CreateAccessCareCard
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} payload
 * @param {any} options = {}
 */
export async function CreateAccessCareCard(cid, payload, options = {}) {
  return await this._post(`/community/${cid}/access/care/cards`, payload, options);
}

/**
 * @function ApiClient#UpdateAccessCareCard
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} cardID
 * @param {any} payload
 * @param {any} options = {}
 */
export async function UpdateAccessCareCard(cid, cardID, payload, options = {}) {
  return await this._put(`/community/${cid}/access/care/cards/${cardID}`, payload, options);
}

/**
 * @function ApiClient#ArchiveAccessCareCard
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} cardID
 * @param {any} options = {}
 */
export async function ArchiveAccessCareCard(cid, cardID, options = {}) {
  return await this._delete(`/community/${cid}/access/care/cards/${cardID}`, options);
}

/**
 * @function ApiClient#RestoreAccessCareCard
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} cardID
 * @param {any} payload
 * @param {any} options = {}
 */
export async function RestoreAccessCareCard(cid, cardID, payload, options = {}) {
  return await this._post(`/community/${cid}/access/care/cards/${cardID}/restore`, payload, options);
}

/**
 * @function ApiClient#PreviewAccessCareCardImport
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} file
 * @param {any} options = {}
 */
export async function PreviewAccessCareCardImport(cid, file, options = {}) {
  return await this._post(`/community/${cid}/access/care/cards/import/preview`, toImportFormData(file), options);
}

/**
 * @function ApiClient#GetAccessCareCardImportPreview
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} previewToken
 * @param {object} params 參數。
 * @param {any} params.page
 * @param {any} params.perPage
 * @param {any} params.status
 * @param {any} options = {}
 */
export async function GetAccessCareCardImportPreview(cid, previewToken, { page = 1, perPage = 50, status } = {}, options = {}) {
  return await this._post(
    `/community/${cid}/access/care/cards/import/preview_rows`,
    previewTokenPayload(previewToken, { Page: page, PerPage: perPage, Status: status }),
    options,
  );
}

/**
 * @function ApiClient#DownloadAccessCareCardImportErrorCSV
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} previewToken
 * @param {any} options = {}
 */
export async function DownloadAccessCareCardImportErrorCSV(cid, previewToken, options = {}) {
  return await postBlob(this, `/community/${cid}/access/care/cards/import/errors.csv`, previewTokenPayload(previewToken), options);
}

/**
 * @function ApiClient#CommitAccessCareCardImport
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} previewToken
 * @param {any} options = {}
 */
export async function CommitAccessCareCardImport(cid, previewToken, options = {}) {
  return await this._post(`/community/${cid}/access/care/cards/import/commit`, previewTokenPayload(previewToken), acceptsAsyncCommit(options));
}

/**
 * @function ApiClient#GetAccessCareTemplates
 * @this {ApiClient}
 * @param {any} cid
 * @param {object} params 參數。
 * @param {any} params.page
 * @param {any} params.perPage
 * @param {any} params.careType
 * @param {any} params.enabled
 * @param {any} options = {}
 */
export async function GetAccessCareTemplates(cid, { page = 1, perPage = 20, careType, enabled } = {}, options = {}) {
  return await this._get(`/community/${cid}/access/care/templates${buildQuery({ p: page, pp: perPage, CareType: careType, Enabled: enabled })}`, options);
}

/**
 * @function ApiClient#GetAccessCareTemplateList
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} params = {}
 * @param {any} options = {}
 */
export async function GetAccessCareTemplateList(cid, params = {}, options = {}) {
  return await this.GetAccessCareTemplates(cid, params, options);
}

/**
 * @function ApiClient#GetAccessCareTemplate
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} templateID
 * @param {any} options = {}
 */
export async function GetAccessCareTemplate(cid, templateID, options = {}) {
  return await this._get(`/community/${cid}/access/care/templates/${templateID}`, options);
}

/**
 * @function ApiClient#GetAccessCareTemplateDetail
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} templateID
 * @param {any} options = {}
 */
export async function GetAccessCareTemplateDetail(cid, templateID, options = {}) {
  return await this.GetAccessCareTemplate(cid, templateID, options);
}

/**
 * @function ApiClient#CreateAccessCareTemplate
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} payload
 * @param {any} options = {}
 */
export async function CreateAccessCareTemplate(cid, payload, options = {}) {
  return await this._post(`/community/${cid}/access/care/templates`, payload, options);
}

/**
 * @function ApiClient#UpdateAccessCareTemplate
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} templateID
 * @param {any} payload
 * @param {any} options = {}
 */
export async function UpdateAccessCareTemplate(cid, templateID, payload, options = {}) {
  return await this._put(`/community/${cid}/access/care/templates/${templateID}`, payload, options);
}

/**
 * @function ApiClient#DisableAccessCareTemplate
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} templateID
 * @param {any} options = {}
 */
export async function DisableAccessCareTemplate(cid, templateID, options = {}) {
  return await this._delete(`/community/${cid}/access/care/templates/${templateID}`, options);
}

/**
 * @function ApiClient#EnableAccessCareTemplate
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} templateID
 * @param {any} options = {}
 */
export async function EnableAccessCareTemplate(cid, templateID, options = {}) {
  return await this._post(`/community/${cid}/access/care/templates/${templateID}/enable`, null, options);
}

/**
 * @function ApiClient#SetAccessCareTemplateEnabled
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} templateID
 * @param {any} enabled
 * @param {any} options = {}
 */
export async function SetAccessCareTemplateEnabled(cid, templateID, enabled, options = {}) {
  return await this._put(`/community/${cid}/access/care/templates/${templateID}/enabled`, { Enabled: enabled }, options);
}

/**
 * @function ApiClient#GetAccessCareApplications
 * @this {ApiClient}
 * @param {any} cid
 * @param {object} params 參數。
 * @param {any} params.page
 * @param {any} params.perPage
 * @param {any} params.status
 * @param {any} params.houseHold
 * @param {any} params.cardSearch
 * @param {any} params.applicantSearch
 * @param {any} options = {}
 */
export async function GetAccessCareApplications(cid, { page = 1, perPage = 20, status, houseHold, cardSearch, applicantSearch } = {}, options = {}) {
  return await this._get(
    `/community/${cid}/access/care/applications${buildQuery({
      p: page,
      pp: perPage,
      Status: status,
      HouseHold: houseHold,
      CardSearch: cardSearch,
      ApplicantSearch: applicantSearch,
    })}`,
    options,
  );
}

/**
 * @function ApiClient#GetAccessCareApplicationList
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} params = {}
 * @param {any} options = {}
 */
export async function GetAccessCareApplicationList(cid, params = {}, options = {}) {
  return await this.GetAccessCareApplications(cid, params, options);
}

/**
 * @function ApiClient#GetAccessCareApplication
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} applicationID
 * @param {any} options = {}
 */
export async function GetAccessCareApplication(cid, applicationID, options = {}) {
  return await this._get(`/community/${cid}/access/care/applications/${applicationID}`, options);
}

/**
 * @function ApiClient#GetAccessCareApplicationDetail
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} applicationID
 * @param {any} options = {}
 */
export async function GetAccessCareApplicationDetail(cid, applicationID, options = {}) {
  return await this.GetAccessCareApplication(cid, applicationID, options);
}

/**
 * @function ApiClient#CreateAccessCareApplication
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} payload
 * @param {any} options = {}
 */
export async function CreateAccessCareApplication(cid, payload, options = {}) {
  return await this._post(`/community/${cid}/access/care/applications`, payload, options);
}

/**
 * @function ApiClient#UpdateAccessCareApplication
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} applicationID
 * @param {any} payload
 * @param {any} options = {}
 */
export async function UpdateAccessCareApplication(cid, applicationID, payload, options = {}) {
  return await this._put(`/community/${cid}/access/care/applications/${applicationID}`, payload, options);
}

/**
 * @function ApiClient#ReviewAccessCareApplication
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} applicationID
 * @param {any} payload
 * @param {any} options = {}
 */
export async function ReviewAccessCareApplication(cid, applicationID, payload, options = {}) {
  return await this._post(`/community/${cid}/access/care/applications/${applicationID}/review`, payload, options);
}

/**
 * @function ApiClient#DownloadAccessCareApplicationFile
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} applicationID
 * @param {any} fileID
 * @param {any} options = {}
 */
export async function DownloadAccessCareApplicationFile(cid, applicationID, fileID, options = {}) {
  return await getBlob(this, `/community/${cid}/access/care/applications/${applicationID}/files/${fileID}`, { ...options, retry: 0 });
}

/**
 * @function ApiClient#GetAccessCareApplicationFile
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} applicationID
 * @param {any} fileID
 * @param {any} options = {}
 */
export async function GetAccessCareApplicationFile(cid, applicationID, fileID, options = {}) {
  return await this.DownloadAccessCareApplicationFile(cid, applicationID, fileID, options);
}

/**
 * @function ApiClient#GetAccessCareIncidents
 * @this {ApiClient}
 * @param {any} cid
 * @param {object} params 參數。
 * @param {any} params.page
 * @param {any} params.perPage
 * @param {any} params.status
 * @param {any} params.incidentType
 * @param {any} params.careType
 * @param {any} params.cardSearch
 * @param {any} params.careCard
 * @param {any} params.houseHold
 * @param {any} params.startDate
 * @param {any} params.endDate
 * @param {any} options = {}
 */
export async function GetAccessCareIncidents(
  cid,
  { page = 1, perPage = 20, status, incidentType, careType, cardSearch, careCard, houseHold, startDate, endDate } = {},
  options = {},
) {
  return await this._get(
    `/community/${cid}/access/care/incidents${buildQuery({
      p: page,
      pp: perPage,
      Status: status,
      IncidentType: incidentType,
      CareType: careType,
      CardSearch: cardSearch,
      CareCard: careCard,
      HouseHold: houseHold,
      StartDate: startDate,
      EndDate: endDate,
    })}`,
    options,
  );
}

/**
 * @function ApiClient#GetAccessCareIncidentList
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} params = {}
 * @param {any} options = {}
 */
export async function GetAccessCareIncidentList(cid, params = {}, options = {}) {
  return await this.GetAccessCareIncidents(cid, params, options);
}

/**
 * @function ApiClient#GetAccessCareIncident
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} incidentID
 * @param {any} options = {}
 */
export async function GetAccessCareIncident(cid, incidentID, options = {}) {
  return await this._get(`/community/${cid}/access/care/incidents/${incidentID}`, options);
}

/**
 * @function ApiClient#GetAccessCareIncidentDetail
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} incidentID
 * @param {any} options = {}
 */
export async function GetAccessCareIncidentDetail(cid, incidentID, options = {}) {
  return await this.GetAccessCareIncident(cid, incidentID, options);
}

/**
 * @function ApiClient#ResolveAccessCareIncident
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} incidentID
 * @param {any} payload
 * @param {any} options = {}
 */
export async function ResolveAccessCareIncident(cid, incidentID, payload, options = {}) {
  return await this._post(`/community/${cid}/access/care/incidents/${incidentID}/resolve`, payload, options);
}

/**
 * @function ApiClient#GetAccessCareIncidentNotificationLogs
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} incidentID
 * @param {object} params 參數。
 * @param {any} params.page
 * @param {any} params.perPage
 * @param {any} params.deliveryStatus
 * @param {any} options = {}
 */
export async function GetAccessCareIncidentNotificationLogs(cid, incidentID, { page = 1, perPage = 20, deliveryStatus } = {}, options = {}) {
  return await this._get(
    `/community/${cid}/access/care/incidents/${incidentID}/notification_logs${buildQuery({
      p: page,
      pp: perPage,
      DeliveryStatus: deliveryStatus,
    })}`,
    options,
  );
}

/**
 * @function ApiClient#GetAccessCareNotificationLogs
 * @this {ApiClient}
 * @param {any} cid
 * @param {object} params 參數。
 * @param {any} params.page
 * @param {any} params.perPage
 * @param {any} params.incident
 * @param {any} params.recipientUser
 * @param {any} params.deliveryStatus
 * @param {any} options = {}
 */
export async function GetAccessCareNotificationLogs(cid, { page = 1, perPage = 20, incident, recipientUser, deliveryStatus } = {}, options = {}) {
  return await this._get(
    `/community/${cid}/access/care/notification_logs${buildQuery({
      p: page,
      pp: perPage,
      Incident: incident,
      RecipientUser: recipientUser,
      DeliveryStatus: deliveryStatus,
    })}`,
    options,
  );
}

/**
 * @function ApiClient#GetAccessCareNotificationLogList
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} params = {}
 * @param {any} options = {}
 */
export async function GetAccessCareNotificationLogList(cid, params = {}, options = {}) {
  return await this.GetAccessCareNotificationLogs(cid, params, options);
}

/**
 * @function ApiClient#GetAccessCareCalendars
 * @this {ApiClient}
 * @param {any} cid
 * @param {object} params 參數。
 * @param {any} params.page
 * @param {any} params.perPage
 * @param {any} params.year
 * @param {any} params.enabled
 * @param {any} options = {}
 */
export async function GetAccessCareCalendars(cid, { page = 1, perPage = 20, year, enabled } = {}, options = {}) {
  return await this._get(
    `/community/${cid}/access/care/calendars${buildQuery({
      p: page,
      pp: perPage,
      CalendarType: "school",
      Year: year,
      Enabled: enabled,
    })}`,
    options,
  );
}

/**
 * @function ApiClient#GetAccessCareCalendarList
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} params = {}
 * @param {any} options = {}
 */
export async function GetAccessCareCalendarList(cid, params = {}, options = {}) {
  return await this.GetAccessCareCalendars(cid, params, options);
}

/**
 * @function ApiClient#GetAccessCareCalendar
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} calendarID
 * @param {any} options = {}
 */
export async function GetAccessCareCalendar(cid, calendarID, options = {}) {
  return await this._get(`/community/${cid}/access/care/calendars/${calendarID}`, options);
}

/**
 * @function ApiClient#GetAccessCareCalendarDetail
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} calendarID
 * @param {any} options = {}
 */
export async function GetAccessCareCalendarDetail(cid, calendarID, options = {}) {
  return await this.GetAccessCareCalendar(cid, calendarID, options);
}

/**
 * @function ApiClient#GetAccessCareNationalHolidayCalendar
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} year
 * @param {any} options = {}
 */
export async function GetAccessCareNationalHolidayCalendar(cid, year, options = {}) {
  return await this._get(`/community/${cid}/access/care/calendars/national/${year}`, options);
}

/**
 * @function ApiClient#GetAccessCareCalendarRecalcJob
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} jobID
 * @param {any} options = {}
 */
export async function GetAccessCareCalendarRecalcJob(cid, jobID, options = {}) {
  return await this._get(`/community/${cid}/access/care/calendars/recalc_jobs/${jobID}`, options);
}

/**
 * @function ApiClient#CreateAccessCareCalendar
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} payload
 * @param {any} options = {}
 */
export async function CreateAccessCareCalendar(cid, payload, options = {}) {
  return await this._post(`/community/${cid}/access/care/calendars`, payload, options);
}

/**
 * @function ApiClient#UpdateAccessCareCalendar
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} calendarID
 * @param {any} payload
 * @param {any} options = {}
 */
export async function UpdateAccessCareCalendar(cid, calendarID, payload, options = {}) {
  return await this._put(`/community/${cid}/access/care/calendars/${calendarID}`, payload, options);
}

/**
 * @function ApiClient#CopyAccessCareCalendarYear
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} calendarID
 * @param {any} payload
 * @param {any} options = {}
 */
export async function CopyAccessCareCalendarYear(cid, calendarID, payload, options = {}) {
  return await this._post(`/community/${cid}/access/care/calendars/${calendarID}/copy_year`, payload, options);
}

/**
 * @function ApiClient#PreviewAccessCareCalendarImport
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} calendarID
 * @param {any} file
 * @param {any} options = {}
 */
export async function PreviewAccessCareCalendarImport(cid, calendarID, file, options = {}) {
  return await this._post(`/community/${cid}/access/care/calendars/${calendarID}/import/preview`, toImportFormData(file), options);
}

/**
 * @function ApiClient#GetAccessCareCalendarImportPreview
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} calendarID
 * @param {any} previewToken
 * @param {object} params 參數。
 * @param {any} params.page
 * @param {any} params.perPage
 * @param {any} params.status
 * @param {any} options = {}
 */
export async function GetAccessCareCalendarImportPreview(cid, calendarID, previewToken, { page = 1, perPage = 50, status } = {}, options = {}) {
  return await this._post(
    `/community/${cid}/access/care/calendars/${calendarID}/import/preview_rows`,
    previewTokenPayload(previewToken, { Page: page, PerPage: perPage, Status: status }),
    options,
  );
}

/**
 * @function ApiClient#DownloadAccessCareCalendarImportErrorCSV
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} calendarID
 * @param {any} previewToken
 * @param {any} options = {}
 */
export async function DownloadAccessCareCalendarImportErrorCSV(cid, calendarID, previewToken, options = {}) {
  return await postBlob(this, `/community/${cid}/access/care/calendars/${calendarID}/import/errors.csv`, previewTokenPayload(previewToken), options);
}

/**
 * @function ApiClient#CommitAccessCareCalendarImport
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} calendarID
 * @param {any} previewToken
 * @param {any} options = {}
 */
export async function CommitAccessCareCalendarImport(cid, calendarID, previewToken, options = {}) {
  return await this._post(
    `/community/${cid}/access/care/calendars/${calendarID}/import/commit`,
    previewTokenPayload(previewToken),
    acceptsAsyncCommit(options),
  );
}
